import type { Equipment } from '../../../api/active/generated/equipment';
import type { PackageDetail } from '../../../api/active/generated/package';
import type { PlanDetail } from '../../../api/active/generated/plan';

const STORAGE_KEY = 'cpq-local-database-demo:v1:catalog';
const CATALOG_CHANGED_EVENT = 'cpq-local-demo-catalog-changed';

export interface LocalCatalogSnapshotDtos {
  equipmentDtos: Equipment[];
  packageDtos: PackageDetail[];
  planDtos: PlanDetail[];
}

export interface LocalCatalogState extends LocalCatalogSnapshotDtos {
  equipmentRevisionDtos: Equipment[];
  packageRevisionDtos: PackageDetail[];
  planRevisionDtos: PlanDetail[];
}

function clone<T>(value: T): T {
  return structuredClone(value);
}

function isArrayOf<T>(value: unknown): value is T[] {
  return Array.isArray(value);
}

export type LocalCatalogSeed = LocalCatalogSnapshotDtos &
  Partial<
    Pick<
      LocalCatalogState,
      'equipmentRevisionDtos' | 'packageRevisionDtos' | 'planRevisionDtos'
    >
  >;

function seedState(seed: LocalCatalogSeed): LocalCatalogState {
  const equipmentDtos = clone(seed.equipmentDtos);
  const packageDtos = clone(seed.packageDtos);
  const planDtos = clone(seed.planDtos);
  return {
    equipmentDtos,
    packageDtos,
    planDtos,
    equipmentRevisionDtos: clone(seed.equipmentRevisionDtos ?? equipmentDtos),
    packageRevisionDtos: clone(seed.packageRevisionDtos ?? packageDtos),
    planRevisionDtos: clone(seed.planRevisionDtos ?? planDtos),
  };
}

/**
 * Load a browser-only rehearsal catalog. Database export data is the initial
 * seed; every later edit stays under this dedicated localStorage key.
 */
export function readLocalCatalogState(
  seed: LocalCatalogSeed,
): LocalCatalogState {
  const initial = seedState(seed);
  if (typeof window === 'undefined') return initial;
  let saved: string | null;
  try {
    saved = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    throw new Error('本地目录演练数据无法读取，请检查浏览器本地存储权限。');
  }
  if (!saved) return initial;
  try {
    const parsed: unknown = JSON.parse(saved);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
      throw new Error();
    const value = parsed as Partial<LocalCatalogState>;
    if (
      !isArrayOf<Equipment>(value.equipmentDtos) ||
      !isArrayOf<PackageDetail>(value.packageDtos) ||
      !isArrayOf<PlanDetail>(value.planDtos) ||
      !isArrayOf<Equipment>(value.equipmentRevisionDtos) ||
      !isArrayOf<PackageDetail>(value.packageRevisionDtos) ||
      !isArrayOf<PlanDetail>(value.planRevisionDtos)
    )
      throw new Error();
    return value as LocalCatalogState;
  } catch {
    throw new Error('本地目录演练数据格式损坏，原始内容已保留。');
  }
}

export function persistLocalCatalogState(state: LocalCatalogState): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  window.dispatchEvent(new CustomEvent(CATALOG_CHANGED_EVENT));
}
